/** Application state, and the actions that move it. */

import { batch, computed, effect, signal } from "@preact/signals";
import { report } from "@transport";

import * as accountsApi from "./accounts";
import * as filters from "./filters";
import * as jmap from "./jmap";
import type { EmailPatch } from "./mutate";
import * as priority from "./priority";
/*
 * `lib/retention.ts` imports this module, so this is a cycle. It is a safe one
 * and worth stating why rather than leaving the next reader to work it out:
 * neither module touches the other at module scope. Retention reads
 * `store.accounts` and `store.toast` inside functions; everything below reads
 * `retention.pinned` inside a computed body or an async function, both of which
 * run long after loading. So whichever of the two is evaluated first, the
 * other's bindings are only ever dereferenced once both are complete.
 *
 * That is also why {@link watchPins} exists rather than a bare `effect` at the
 * foot of this file. An effect runs its body the moment it is created, and a
 * module-scope one here would read `retention.pinned` during whichever of the
 * two modules is still initialising -- which is a `ReferenceError` on the
 * import line, on exactly the load order the tests happen to use.
 *
 * The alternative was a third module owning the pinned set, which would have
 * put the ids somewhere neither the thing that writes them nor the thing that
 * renders them lives.
 */
import * as retention from "./retention";
import * as reminders from "./reminders";
import type { Email, Envelope, Mailbox, SearchSnippet, Session } from "./types";

export const PAGE = 40;

/**
 * Ceiling on how much a quiet refresh re-fetches per account.
 *
 * The session advertises `maxObjectsInGet` (500 on this daemon) and a refresh
 * that re-reads everything already on screen would sail past it on a list
 * someone has scrolled a long way into. Past this depth the refresh keeps the
 * rows it has rather than replacing them.
 */
const MAX_REFETCH = 500;

export type View = "mail" | "accounts" | "settings";

/**
 * What the middle column is showing.
 *
 * Two of these four are a folder and two are not, and that distinction is what
 * the rest of the file keeps having to make. `unified` and `mailbox` name a
 * mailbox in each account they span, so membership is "is this message filed
 * there"; `starred` and `saved` name a property the message carries around with
 * it, so membership has nothing to do with where it is filed.
 */
export type Scope =
  | { kind: "unified"; role: string }
  | { kind: "mailbox"; accountId: string; mailboxId: string }
  /**
   * Every flagged message, in every folder, in every account.
   *
   * Deliberately not `{ kind: "unified"; role: "flagged" }`, which is what the
   * sidebar used to offer and what an unwary reading of RFC 8621 suggests. That
   * would be a *mailbox* with a role, and a star is not a mailbox: it is a
   * keyword the message carries wherever it is filed. Almost no provider has
   * such a folder -- so the old entry never appeared at all -- and on one that
   * does it would hold strictly less than this. So the view is a query with no
   * `inMailbox` and a `hasKeyword`; see {@link specFor}.
   */
  | { kind: "starred" }
  /**
   * Every message with a reminder that is not done, soonest first.
   *
   * Server state (`lib/reminders.ts`), so like `saved` it is not an
   * `Email/query`: the ids come from the reminders list and are fetched by
   * name -- see {@link fetchReminders}. Unlike `saved` it is the same on every
   * device, because the daemon holds it.
   */
  | { kind: "reminders" }
  /**
   * Every message this device has promised to keep readable with no network.
   *
   * The one view in this app that is not an `Email/query`, and it cannot be
   * one: a pin lives in the local replica and the daemon has never heard of it,
   * so there is no condition to send that would select these messages. The ids
   * come out of `lib/retention.ts` and are fetched by name -- see
   * {@link fetchSaved}, which also explains why this scope has no paging.
   *
   * Desktop only. `retention.available` is false in a browser and the sidebar
   * leaves the entry out rather than drawing one that cannot work.
   */
  | { kind: "saved" };

/**
 * What the two cross-folder views are called, in the one place they are named.
 *
 * Three components put these words on screen -- the sidebar entry, the sentence
 * over a filtered list, and the search placeholder -- and they have to be the
 * same words in all three. "No unread messages in Starred" under a sidebar row
 * saying something else is the reader being told about a place they cannot find.
 *
 * `KEPT` is "Saved", Amit's word, and the argument that first put "Kept
 * offline" here is the argument for changing it: one feature gets one noun, and
 * the app had three. The reading pane said "Keep offline", the same control
 * said "Saving for offline…" while it worked, and the settings page said
 * "Pinned messages are kept whatever the limit says" -- kept, saved and pinned,
 * for one idea. They all say "saved" now.
 *
 * What it does *not* rename is the settings page id `offline`, which is URL
 * surface: somebody has that address. The label above it changed; the address
 * did not.
 */
export const STARRED_LABEL = "Starred";
export const KEPT_LABEL = "Saved";
export const REMINDERS_LABEL = "Reminders";

export const view = signal<View>("mail");
export const session = signal<Session | null>(null);
export const startupError = signal<string | null>(null);
export const connecting = signal(false);

export const mailboxes = signal<Map<string, Mailbox[]>>(new Map());
export const scope = signal<Scope>({ kind: "unified", role: "inbox" });
export const search = signal("");

/**
 * Whether the view in front of the reader has two tabs at all.
 *
 * The inbox, and only the inbox. Archive and Sent are not split -- the rules
 * are about mail arriving, and a low-priority tab over Sent would be two tabs
 * where one of them is always empty. A search drops the split for a different
 * reason: the focused/low tabs are two readings of the inbox, and confining a
 * search to one of them would tell somebody there are no results in the tab
 * they happen to be standing in while a demoted newsletter sits in the other.
 * (The search still stays in the inbox -- the bucket is a keyword within it,
 * not another folder -- so dropping the split does not widen it to Sent.)
 *
 * Starred and Saved are excluded for the same reason as a search, and it
 * matters more there than it looks. Both span every folder, so "which tab" has
 * no answer -- and a `lowPriority: false` sent from either would silently drop
 * every demoted sender's starred or pinned mail out of a view whose entire
 * promise is that it holds all of it. There is no tab strip in those views for
 * anyone to find it under afterwards.
 *
 * When this is false no bucket condition is sent, which is what makes those
 * views show everything.
 */
export const hasBuckets = computed<boolean>(() => {
  if (search.value.trim()) return false;
  const s = scope.value;
  if (s.kind === "unified") return s.role === "inbox";
  if (s.kind === "mailbox") {
    return mailboxes.value.get(s.accountId)?.find((m) => m.id === s.mailboxId)?.role === "inbox";
  }
  return false;
});

/** The tab actually in force: `focused` wherever there are no tabs. */
export const activeBucket = computed<priority.Bucket>(() =>
  hasBuckets.value ? priority.bucket.value : "focused",
);

export const rows = signal<Envelope[]>([]);
/**
 * How many messages match, across every account, as the server counted them.
 *
 * Not `rows.length`, which is only what has been paged in so far and climbs as
 * someone scrolls. The number beside an active filter is the one thing that
 * answers "is my mail missing or merely hidden", so it has to be the whole
 * count and it has to hold still. `undefined` while it is unknown -- an account
 * that failed cannot be counted, and guessing would be worse than saying
 * nothing.
 */
export const matchTotal = signal<number | undefined>(undefined);
export const snippets = signal<Map<string, SearchSnippet>>(new Map());
export const loading = signal(false);
export const loadingMore = signal(false);
export const listError = signal<string | null>(null);
/** Per-account failures during a merged query: shown without blanking the list. */
export const partialErrors = signal<Map<string, string>>(new Map());

export const openEmail = signal<Envelope | null>(null);
export const openEmailId = signal<string | null>(null);
export const openError = signal<string | null>(null);
export const openLoading = signal(false);

export const composeOpen = signal(false);

export const accounts = computed(() => {
  const s = session.value;
  if (!s) return [] as { id: string; name: string; isReadOnly: boolean }[];
  return Object.entries(s.accounts)
    .map(([id, a]) => ({ id, name: a.name, isReadOnly: a.isReadOnly }))
    .sort((a, b) => a.name.localeCompare(b.name));
});

/** True while every account the server knows about refuses writes. */
export const readOnly = computed(() => accounts.value.every((a) => a.isReadOnly));

/**
 * Each account's colour, keyed by the JMAP account id.
 *
 * A second map rather than a field on `accounts`, because the two facts come
 * from different documents. The session says which accounts exist; `/accounts`
 * says what colour each one is, and it is the more expensive of the two -- it
 * counts messages and folders and opens the tenant's secrets file to answer.
 * The session is re-read on every push event, and re-reading `/accounts` that
 * often to learn a word that changes once a year would be an odd trade.
 *
 * So it is read once at startup and written again by the accounts screen, which
 * is the only place a colour can change. The keys are `String(id)`: a JMAP
 * `accountId` is the decimal spelling of the same `accounts.id` that
 * `/accounts` returns, which is what makes a colour reachable from a message
 * row without any new plumbing.
 *
 * The cost of that trade is a colour changed in another tab, which this one
 * keeps showing until it reloads. Nothing is wrong on screen in the meantime,
 * just a page behind.
 */
export const accountColours = signal<Map<string, string>>(new Map());

/** The colour of one account, or undefined if it has none we know about. */
export function colourOf(accountId: string): string | undefined {
  return accountColours.value.get(accountId);
}

/** Publish colours read from `/accounts`, so the mail views follow the picker. */
export function setAccountColours(rows: { id: number; colour: string }[]): void {
  accountColours.value = new Map(rows.map((a) => [String(a.id), a.colour]));
}

async function loadAccountColours(): Promise<void> {
  try {
    setAccountColours(await accountsApi.list());
  } catch {
    // Decoration. A mail client that would not show anyone their mail because
    // it could not find out what colour to draw beside it is a worse client
    // than one with no colours at all.
  }
}

/**
 * How much mail the current scope holds before any filtering, or `undefined`
 * when that cannot be known.
 *
 * Read off the mailbox counters the sidebar already has, so it costs no
 * request. It exists for one sentence: an empty filtered list has to be able to
 * tell "this mailbox is full of mail you have filtered out" from "this mailbox
 * is empty", and those are the same screen otherwise. Claiming the filters are
 * hiding something from someone looking at an empty Trash would be its own
 * small lie, and a client that lies about small things does not get believed
 * about large ones.
 *
 * `undefined` while a search is running: a search is not confined to a mailbox
 * -- `specFor` drops the mailbox from a unified query the moment there is text
 * -- so no counter here is an answer to what the reader is looking at.
 *
 * `undefined` for Starred too, and this is the one that is worth resisting the
 * urge to fill in. Nothing in the mailbox data answers "how many flagged
 * messages are there across every folder": the counters are per mailbox and
 * count no keyword. The only way to get the number is another query per
 * account, paid on every refresh, to decorate a sentence -- and a number
 * assembled out of the wrong counters would be worse than the silence.
 *
 * Saved is the opposite case and does answer, because the pinned set is
 * the whole population and it is already in hand. So "8 kept here, none of them
 * unread" is a sentence that view can honestly say.
 */
export const scopeTotal = computed<number | undefined>(() => {
  const s = scope.value;
  if (s.kind === "mailbox") {
    return mailboxes.value.get(s.accountId)?.find((m) => m.id === s.mailboxId)?.totalEmails;
  }
  // `retention.pinnedCount` is the wrong number here by a hair: it counts every
  // pin, and this view can only show the ones belonging to an account the
  // session still knows about. Counting what will actually be asked for keeps
  // "3 kept here, none of them unread" from being a sentence about four.
  if (s.kind === "saved") {
    let kept = 0;
    for (const ids of pinnedByAccount().values()) kept += ids.length;
    return kept;
  }
  if (s.kind === "reminders") {
    let due = 0;
    for (const ids of remindersByAccount().values()) due += ids.length;
    return due;
  }
  if (s.kind === "starred") return undefined;
  if (search.value.trim()) return undefined;
  let n = 0;
  let known = false;
  for (const a of accounts.value) {
    const box = mailboxes.value.get(a.id)?.find((m) => m.role === s.role);
    if (!box) continue;
    n += box.totalEmails;
    known = true;
  }
  return known ? n : undefined;
});

/**
 * The unread count that gets the loud treatment: focused inbox only.
 *
 * Low-priority mail is subtracted rather than counted, and that is the whole
 * point of giving it a bucket. A badge that includes mail the reader has
 * already said they do not want to be interrupted by is a badge that never goes
 * down, and a badge that never goes down is one people stop reading -- taking
 * the real unread count with it.
 *
 * `unreadLowPriorityTotal` is the same number for the other tab, reported
 * quietly beside it. Hiding it entirely would be the opposite mistake: mail
 * nobody can see the existence of is mail that gets missed, and "low priority"
 * was never meant to mean "invisible".
 */
export const unreadTotal = computed(() => {
  let n = 0;
  for (const list of mailboxes.value.values()) {
    for (const m of list) {
      if (m.role !== "inbox") continue;
      n += m.unreadEmails - (m.lowPriorityUnreadEmails ?? 0);
    }
  }
  // Clamped because the two numbers come from one query but travel as two
  // fields, and a daemon mid-upgrade could in principle send a pair that does
  // not subtract. A negative badge is a worse answer than a slightly stale one.
  return Math.max(0, n);
});

export const unreadLowPriorityTotal = computed(() => {
  let n = 0;
  for (const list of mailboxes.value.values()) {
    for (const m of list) if (m.role === "inbox") n += m.lowPriorityUnreadEmails ?? 0;
  }
  return n;
});

/**
 * The same split for the mailbox the reader is actually looking at.
 *
 * Unread only. A total was tried here and taken back out: on an inbox read to
 * the bottom it put a number beside both tabs permanently, and a count that is
 * always there is one nobody reads. The tabs are for "what still wants me",
 * and an empty tab saying nothing is the correct answer to that.
 */
export const bucketCounts = computed<{ focused: number; low: number } | undefined>(() => {
  if (!hasBuckets.value) return undefined;
  const s = scope.value;
  // `hasBuckets` is already false for anything that is not an inbox, so the
  // other two kinds cannot reach this. Said again in code because the compiler
  // cannot see through the computed, and because the honest answer if the two
  // ever disagreed is "no tabs" rather than a count assembled from whichever
  // mailboxes happened to be to hand.
  if (s.kind === "starred" || s.kind === "saved" || s.kind === "reminders") return undefined;
  const boxes =
    s.kind === "mailbox"
      ? [mailboxes.value.get(s.accountId)?.find((m) => m.id === s.mailboxId)]
      : accounts.value.map((a) => mailboxes.value.get(a.id)?.find((m) => m.role === s.role));
  let focused = 0;
  let low = 0;
  let known = false;
  for (const box of boxes) {
    if (!box) continue;
    known = true;
    const lowUnread = box.lowPriorityUnreadEmails ?? 0;
    focused += Math.max(0, box.unreadEmails - lowUnread);
    low += lowUnread;
  }
  return known ? { focused, low } : undefined;
});

// ---- toasts -----------------------------------------------------------------

export interface Toast {
  id: number;
  text: string;
  kind: "error" | "info";
  /**
   * One thing the reader can do about it, usually Undo.
   *
   * On the toast rather than in a bar of its own because the offer has a
   * deadline: it is worth showing for as long as the sentence explaining it is
   * on screen and not a second longer, and a control that outlives its
   * explanation is a control nobody can predict.
   */
  action?: { label: string; run: () => void };
}
export const toasts = signal<Toast[]>([]);
let toastId = 0;

export function toast(
  text: string,
  kind: Toast["kind"] = "info",
  action?: Toast["action"],
): number {
  const id = (toastId += 1);
  toasts.value = [...toasts.value, { id, text, kind, ...(action ? { action } : {}) }];
  // An offer to undo needs long enough to read the sentence, notice the button
  // and reach it; four seconds is not that. Nine, the same as an error.
  setTimeout(() => dismiss(id), kind === "error" || action ? 9000 : 4000);
  return id;
}

export function dismiss(id: number): void {
  toasts.value = toasts.value.filter((t) => t.id !== id);
}

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// ---- startup ----------------------------------------------------------------

/**
 * Fetch the session document and everything that hangs off it.
 *
 * No longer checks for a token first. It used to send the reader to Settings
 * whenever `deviceToken` was an empty string, which was the client's entire
 * notion of "not signed in" — a string-emptiness test that could not tell a
 * revoked credential from a good one, and had nothing to say about the cookie
 * that is now the usual credential. Whether anyone is signed in is
 * `auth.authState`'s question, and the app gates on it before this runs.
 */
export async function connect(): Promise<void> {
  connecting.value = true;
  startupError.value = null;
  // A session is being established from scratch, which includes signing in as
  // somebody else. Whatever states are held describe another tenant's counter
  // and another tenant's account ids, and an id can mean a different account in
  // each -- so they go before anything is read, rather than being trusted
  // because the id matched.
  forgetStates();
  try {
    const s = await jmap.getSession();
    const boxes = await jmap.getMailboxes(Object.keys(s.accounts));
    batch(() => {
      session.value = s;
      mailboxes.value = adoptFolders(boxes);
      // This used to leave the settings screen the moment a session with
      // accounts in it arrived, from when settings was where an account was
      // added and being on it meant there was nothing else to show. Accounts
      // are a settings page again, and the rule now says the opposite of what
      // it meant: it would throw someone off the Accounts page as the account
      // they had just connected finished loading, and it would undo the
      // `#/settings/...` a reload had just restored. Nothing puts anyone on
      // settings by itself, so there is no longer anything to correct.
    });
    // Not awaited: the colours decorate a list that should already be drawing.
    void loadAccountColours();
    await reload();
  } catch (e) {
    startupError.value = message(e);
    session.value = null;
  } finally {
    connecting.value = false;
  }
}

/** Re-read the session and the mailbox list. */
export async function refreshSession(): Promise<void> {
  try {
    const s = await jmap.getSession();
    const boxes = await jmap.getMailboxes(Object.keys(s.accounts));
    batch(() => {
      session.value = s;
      mailboxes.value = adoptFolders(boxes);
    });
  } catch {
    // A failed background refresh is not worth a toast; the next user action
    // will surface the same problem with context.
  }
}

// ---- what the server has already told us -------------------------------------

/*
 * Two state strings per account, and neither is a signal.
 *
 * They are the arguments the delta path sends -- `sinceQueryState` for the
 * list, `sinceState` for the folders -- and nothing renders either of them.
 * Made signals they would change on every update and invalidate every computed
 * in the app for the sake of a string nobody draws.
 *
 * They are kept *apart* on purpose, although this daemon derives both from one
 * counter. They answer about different reads: the mailboxes were fetched at one
 * moment and the list at another, and a client that used the later of the two
 * as though it were the earlier would ask for changes since a point it had
 * never seen and be told, correctly, that nothing had happened.
 *
 * An account with no state is an account that cannot be asked a delta question
 * at all -- its last read failed -- and `lib/delta.ts` falls back to a full
 * reload rather than guessing at zero, which would mean "everything, since the
 * beginning of the log".
 */
const listStates = new Map<string, string>();
const folderStates = new Map<string, string>();

function adoptFolders(pages: Map<string, jmap.MailboxPage>): Map<string, Mailbox[]> {
  const out = new Map<string, Mailbox[]>();
  for (const [accountId, page] of pages) {
    out.set(accountId, page.list);
    if (page.state) folderStates.set(accountId, page.state);
    else folderStates.delete(accountId);
  }
  // An account that has left the session should not leave a state behind for a
  // future account to inherit by reusing its id.
  for (const accountId of [...folderStates.keys()]) {
    if (!pages.has(accountId)) folderStates.delete(accountId);
  }
  return out;
}

/** The query state each account's on-screen list was read at. */
export function listStateOf(accountId: string): string | undefined {
  return listStates.get(accountId);
}

/** The state each account's folder list was read at. */
export function folderStateOf(accountId: string): string | undefined {
  return folderStates.get(accountId);
}

/** Record a state the delta path has caught up to without refetching. */
export function noteListState(accountId: string, state: string): void {
  listStates.set(accountId, state);
}

export function noteFolderState(accountId: string, state: string): void {
  folderStates.set(accountId, state);
}

/** Forget every state, so the next update goes the long way round. */
function forgetStates(): void {
  listStates.clear();
  folderStates.clear();
}

/**
 * Ask the server everything again: the session, the mailboxes, then the list.
 *
 * This is what "refresh" has to mean, and getting it wrong is how a working
 * client looks broken. Every mailbox the sidebar draws comes from the session
 * document, which was read once at startup -- and adding a mail account is a
 * CLI action, because the daemon has no HTTP endpoint for it yet. So an account
 * added on the server after the page loaded is invisible to a refresh that only
 * re-runs the query it already knows how to run: no Inbox appears, no mailbox
 * appears, and the only cure is a reload the client never suggests.
 *
 * `quiet` is for a refresh nobody asked for -- the one the live stream triggers
 * when the server says something changed. It keeps the rows already on screen
 * instead of blanking the list under someone mid-read, and re-fetches to the
 * depth they have scrolled to rather than snapping back to the first page.
 */
export async function refresh(opts: { quiet?: boolean } = {}): Promise<void> {
  await refreshSession();
  await reload(opts);
}

// ---- the merged list --------------------------------------------------------

/**
 * Per-account paging state.
 *
 * A unified list is a merge of several independently paged queries, so the
 * cursors cannot be one number. Each account keeps its own position and its
 * own buffer of everything fetched so far.
 */
interface Cursor {
  position: number;
  exhausted: boolean;
  buffer: Email[];
  /** What this account's last query said the whole result set holds. */
  total: number | undefined;
}
let cursors = new Map<string, Cursor>();

/**
 * Which accounts a view spans.
 *
 * The filter on the role is load-bearing and it used to be missing, which was a
 * real bug with a very confusing face on it. `specFor` asks `roleMailbox` for
 * the folder to query and gets `undefined` from an account that has no mailbox
 * with that role -- and a query with no `inMailbox` is not "no messages", it is
 * *every message in the account*. So a unified Sent, over one account with a
 * Sent folder and one without, returned that second account's entire archive:
 * eleven thousand received messages presented as things the user had sent. The
 * complaint it produced was that the unified Sent view "all seem like incoming
 * mail messages", and they were.
 *
 * An account with no mailbox for the role contributes nothing, which is what
 * `inScope` has always said this means. Dropped here rather than patched in
 * `specFor` because the account is not in the view at all: giving it a cursor
 * would also give it a page to load and an error to report.
 *
 * A search is the exception, and deliberately: it spans whole accounts rather
 * than one folder, so every account is in it whatever roles it has. Starred is
 * the same exception for the same reason -- a star is a keyword rather than a
 * folder, so an account with no special folders at all can still hold one.
 *
 * **Empty for Saved, and that is not the same as "no accounts".** This
 * computed has one other reader, `lib/delta.ts`, and what it asks it is "which
 * accounts can I take an `Email/queryChanges` against for the list on screen".
 * The saved view is not a query, so the honest answer is none: there is no
 * filter to probe with, no query state was ever recorded for it, and a probe
 * built from a scope that is not a query would ask about the *whole account* --
 * which would mean every event during a backfill reloading a view that a
 * backfill cannot possibly have changed. {@link targets} is what the fetch path
 * asks instead, and it answers with the accounts that actually hold pins.
 */
export const scopeAccounts = computed<string[]>(() => {
  const s = scope.value;
  if (s.kind === "saved" || s.kind === "reminders") return [];
  if (s.kind === "mailbox") return [s.accountId];
  const ids = accounts.value.map((a) => a.id);
  if (s.kind === "starred") return ids;
  if (search.value.trim()) return ids;
  return ids.filter((id) => roleMailbox(id, s.role));
});

/** Which accounts the *fetch* covers, which is the query's set except for pins. */
function targets(): string[] {
  const s = scope.value;
  if (s.kind === "saved") return [...pinnedByAccount().keys()];
  if (s.kind === "reminders") return [...remindersByAccount().keys()];
  return scopeAccounts.value;
}

/**
 * The messages with a live reminder, by account, for accounts the session
 * still knows. The same shape as {@link pinnedByAccount} for the same reason:
 * the list is fetched by name, one `Email/get` per account.
 */
function remindersByAccount(): Map<string, string[]> {
  const known = new Set(accounts.value.map((a) => a.id));
  const out = new Map<string, string[]>();
  for (const r of reminders.items.value) {
    if (!known.has(r.accountId)) continue;
    const ids = out.get(r.accountId);
    if (ids) ids.push(r.emailId);
    else out.set(r.accountId, [r.emailId]);
  }
  return out;
}

/**
 * The pinned messages, split into the per-account `Email/get` calls they need.
 *
 * **These are JMAP ids.** `retention.pinned` holds `accountId:emailId` keys
 * built from ids the local replica has already translated back out of its own
 * row numbering -- see `pinned_messages` on the Rust side -- so they are the
 * ids `Email/get` takes and the ids a URL carries. Confusing the two id spaces
 * is this project's most dangerous bug class, so it is worth saying at the one
 * place they cross into a request.
 *
 * Accounts the session does not know about are dropped rather than queried: a
 * pin can outlive the account it was made in, and asking a daemon about an
 * account id it has never issued is a refusal, not an empty answer.
 */
function pinnedByAccount(): Map<string, string[]> {
  const known = new Set(accounts.value.map((a) => a.id));
  const out = new Map<string, string[]>();
  for (const key of retention.pinned.value) {
    // The first colon, not the last: `keyOf` joins with one and RFC 8620
    // restricts both halves to `A-Za-z0-9_-`, so there is only ever one.
    const cut = key.indexOf(":");
    if (cut < 1) continue;
    const accountId = key.slice(0, cut);
    if (!known.has(accountId)) continue;
    const ids = out.get(accountId);
    if (ids) ids.push(key.slice(cut + 1));
    else out.set(accountId, [key.slice(cut + 1)]);
  }
  return out;
}

/**
 * The mailbox in `accountId` with this role, if it has one.
 *
 * Exported because it is also the question every move asks. Archive, Trash and
 * Junk are not verbs the protocol has -- they are mailbox ids -- and plenty of
 * IMAP servers have no Archive at all, so "which mailbox" is a question that
 * legitimately answers `undefined` and every caller has to cope with that.
 */
export function roleMailbox(accountId: string, role: string): string | undefined {
  return mailboxes.value.get(accountId)?.find((m) => m.role === role)?.id;
}

/**
 * Every mailbox's role, keyed `accountId:mailboxId`.
 *
 * Built once per change to the mailbox list rather than searched per row: a
 * merged list is hundreds of rows and each one needs this answer, and a linear
 * scan of an account's folders inside a render loop is the kind of thing that
 * only shows up as jank on the account with two hundred folders.
 */
const mailboxRoles = computed(() => {
  const out = new Map<string, string>();
  for (const [accountId, list] of mailboxes.value) {
    for (const m of list) if (m.role) out.set(`${accountId}:${m.id}`, m.role);
  }
  return out;
});

/**
 * The role of the mailbox a message is filed in: `sent`, `drafts`, `inbox`…
 *
 * Asked of the *message* rather than of the view, and that is the whole point.
 * `scope` knows the role only for a unified view, so keying off it left a
 * single account's own Sent folder rendering as incoming mail -- and it has no
 * answer at all for a search, which returns mail the reader sent and mail they
 * received in one list. Each row deciding for itself is the only version that
 * is right in all three, and `mailboxIds` is already on every row, so it costs
 * a map lookup and no request.
 *
 * `undefined` for a folder with no role, which is most of them: a user's own
 * "Receipts" is not outgoing and not special.
 */
export function roleOf(accountId: string, email: Email): string | undefined {
  const roles = mailboxRoles.value;
  for (const id of Object.keys(email.mailboxIds)) {
    if (!email.mailboxIds[id]) continue;
    const role = roles.get(`${accountId}:${id}`);
    if (role) return role;
  }
  return undefined;
}

function specFor(accountId: string, position: number, limit: number): jmap.QuerySpec {
  const s = scope.value;
  const text = search.value.trim();
  // Refused rather than answered with something plausible. There is no query
  // that selects pinned messages -- the daemon does not know which they are --
  // and every "safe" spec this could return would be wrong in the expensive
  // direction: an empty filter is not "no messages", it is *every message in
  // the account*, which is exactly how a unified Sent once returned somebody's
  // whole archive. `reload` sends this scope to `fetchSaved` and `targets`
  // keeps it off the delta path, so reaching here is a wiring mistake and
  // should read as one.
  if (s.kind === "saved") {
    throw new Error("the kept-offline view is not a query; fetchSaved answers it");
  }
  if (s.kind === "reminders") {
    throw new Error("the reminders view is not a query; fetchReminders answers it");
  }
  const mailboxId =
    s.kind === "mailbox"
      ? s.mailboxId
      : // A search stays in the view it was run from: searching the unified
        // inbox searches each account's inbox, not Sent and Spam too (Amit,
        // 2026-09-23 -- it used to span the whole account). The bucket split is
        // still dropped for a search (see `hasBuckets`), so a demoted inbox
        // newsletter is still found -- it lives in the inbox, just under the
        // other tab. Starred alone names no mailbox: it spans every folder by
        // construction, see the note on the scope.
        s.kind === "starred"
        ? undefined
        : roleMailbox(accountId, s.role);
  // Every account gets the same conditions. A unified view is N queries, and a
  // filter that reached four of five accounts would not be a partial answer --
  // it would be a wrong one, a list claiming to be unread mail with one
  // account's read mail mixed into it.
  // The bucket, only where there are two of them. `hasBuckets` is what decides
  // that; sending `lowPriority: false` from Archive would quietly hide a
  // demoted sender's mail from a folder that has no tab to find it under.
  const conditions: Record<string, unknown> = { ...filters.conditions(filters.filters.value) };
  if (hasBuckets.value) conditions["lowPriority"] = priority.bucket.value === "low";
  // What makes Starred the view it is, and it lands on the same key
  // `filters.conditions` writes when the Flagged chip is on. That collision is
  // deliberate and harmless -- both want the string `$flagged`, so the merge
  // cannot produce a query either of them would disagree with, and the order
  // of these two lines does not matter. The chip is deliberately still offered
  // here rather than hidden: filters are a way of reading that survives
  // changing view, and taking the control away in one view would also take
  // away the only place its state can be turned off from.
  if (s.kind === "starred") conditions["hasKeyword"] = "$flagged";
  return {
    accountId,
    mailboxId,
    text: text || undefined,
    // A search is for reading, and a draft in its results opens the composer
    // instead -- so drafts stay out of search and are found where they live,
    // in Drafts. Every other scope shows what its folder holds.
    excludeDrafts: Boolean(text) || undefined,
    conditions,
    position,
    limit,
  };
}

/** The query one account's list came from, as it stands now. */
export function currentSpec(accountId: string): jmap.QuerySpec {
  return specFor(accountId, 0, PAGE);
}

/**
 * Every message this view depends on, by `accountId:id`.
 *
 * The question it answers is "would a change to this message be visible", and
 * that is wider than the rows on screen. The buffers hold messages the merge
 * cutoff is withholding, and one of them becoming the newest thing in its
 * account would put it on screen without anything being fetched. The selection
 * and the open message are held whether or not they are still in the list at
 * all -- a bulk action reads the selection's copy, so a stale one there sends
 * a mailbox id the server will refuse. And a reprieved row is on screen *by
 * construction* because it no longer matches, so nothing else here would name
 * it.
 *
 * Used by `lib/delta.ts` to decide whether a change is worth a refetch. Erring
 * wide is the safe direction: a key too many costs one reload, a key too few
 * costs a row that silently stops being true.
 */
export function heldKeys(): Set<string> {
  const keys = new Set<string>();
  for (const [accountId, cursorState] of cursors) {
    for (const email of cursorState.buffer) keys.add(`${accountId}:${email.id}`);
  }
  for (const row of rows.peek()) keys.add(keyOf(row));
  for (const key of selection.peek().keys()) keys.add(key);
  for (const key of reprieved.peek().keys()) keys.add(key);
  const open = openEmailId.peek();
  if (open) keys.add(open);
  return keys;
}

/**
 * The oldest message this account is showing, while it has more to show.
 *
 * `undefined` once an account is exhausted, and the difference matters: an
 * exhausted account's list *is* the whole result, so a message joining it
 * anywhere is on screen, and telling the server to ignore insertions past the
 * last row would hide the arrival of older mail from an import.
 */
export function oldestHeld(accountId: string): string | undefined {
  const cursorState = cursors.get(accountId);
  if (!cursorState || cursorState.exhausted) return undefined;
  return cursorState.buffer[cursorState.buffer.length - 1]?.id;
}

/**
 * Replace some of one account's folders with fresher copies.
 *
 * The cheap half of an update: a backfill moves counters and nothing else, and
 * four integers per touched folder is a different order of cost from re-reading
 * every mailbox in every account. A folder that arrives without being known is
 * appended rather than dropped, because the alternative is a mailbox the
 * sidebar cannot draw until something else refetches.
 */
export function patchFolders(accountId: string, fresh: Mailbox[]): void {
  if (fresh.length === 0) return;
  const current = mailboxes.value.get(accountId);
  if (!current) return;
  const byId = new Map(fresh.map((m) => [m.id, m]));
  const merged = current.map((m) => byId.get(m.id) ?? m);
  const known = new Set(current.map((m) => m.id));
  for (const m of fresh) if (!known.has(m.id)) merged.push(m);
  const next = new Map(mailboxes.value);
  next.set(accountId, merged);
  mailboxes.value = next;
}

/**
 * Messages held in the list although they no longer match the filters.
 *
 * The bug this exists to prevent: "unread" is on, the reader opens a message,
 * the client marks it read, and the row they are reading vanishes from under
 * the cursor -- taking with it any sense of where they were in the list. The
 * same happens on `s` with "flagged" on. A filter is a description of the list
 * the reader asked for, not a rule the list has to keep enforcing against them
 * while they work through it.
 *
 * So a message that stops matching *because of something the reader just did*
 * stays where it is, marked, and the row says why. It goes when the list is
 * asked for again: a new folder, a new search, a change of filter, or the
 * refresh button. Notably it does *not* go on the refresh the live stream
 * triggers, which is the whole difference between a list that settles and one
 * that rearranges itself every few seconds.
 *
 * Only messages the reader acted on land here. Mail that arrives already
 * failing the filter was never in the list to begin with, and the server simply
 * does not return it.
 */
export const reprieved = signal<Map<string, Envelope>>(new Map());

/**
 * Merge the buffers into the displayed list.
 *
 * The cutoff is what makes the merge honest. An account that still has pages
 * left cannot vouch for anything older than the oldest row it has produced, so
 * rows older than the *newest* such boundary are held back rather than shown
 * in a position a later page would contradict. Once every account is
 * exhausted there is no boundary and everything is shown.
 *
 * Reprieved rows are put back after the buffers, because a refresh rebuilds the
 * buffers from the server and the server has no idea the reader is still
 * looking at that message. They obey the same cutoff as everything else: a row
 * that cannot honestly be placed is still a row that cannot honestly be placed.
 */
function merge(): Envelope[] {
  let cutoff = -Infinity;
  for (const [, cursor] of cursors) {
    if (cursor.exhausted) continue;
    const oldest = cursor.buffer[cursor.buffer.length - 1];
    if (!oldest) continue;
    cutoff = Math.max(cutoff, Date.parse(oldest.receivedAt));
  }
  const out: Envelope[] = [];
  const seen = new Set<string>();
  for (const [accountId, cursor] of cursors) {
    for (const email of cursor.buffer) {
      if (Date.parse(email.receivedAt) < cutoff) continue;
      // A buffer is not trusted to hold a message once, and that is a statement
      // about this function rather than about its callers: whatever else is
      // wrong, the list must never draw the same message twice. It has now been
      // drawn twice for two unrelated reasons -- overlapping reloads, then a
      // page fetched inside a quiet reload -- and both times the reader met a
      // duplicated row while the folder badge, counting the messages rather
      // than the rows, correctly said one. Each cause is fixed at its source;
      // this is the invariant that means the *next* one is a wasted request
      // instead of a visible defect.
      const key = `${accountId}:${email.id}`;
      if (seen.has(key)) continue;
      out.push({ accountId, email });
      seen.add(key);
    }
  }
  for (const [key, row] of reprieved.value) {
    if (seen.has(key) || !cursors.has(row.accountId)) continue;
    if (Date.parse(row.email.receivedAt) < cutoff) continue;
    out.push(row);
  }
  out.sort((a, b) => Date.parse(b.email.receivedAt) - Date.parse(a.email.receivedAt));
  return out;
}

/**
 * Structural equality over the JSON-shaped values a list row is made of.
 *
 * Not keyed to a list of fields on purpose: a field list would go quietly
 * stale the day `LIST_PROPERTIES` grows, and the failure mode of a missed
 * field is a row on screen that no longer matches what the server said. The
 * failure mode of comparing too much is one row re-rendered for nothing,
 * which is the safe direction.
 */
function sameJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => sameJson(v, b[i]));
  }
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => sameJson((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}

/**
 * Put a merged list on screen without pretending everything in it is new.
 *
 * `merge` builds fresh envelope objects every time it runs, so publishing its
 * output directly hands the renderer a list in which every row has a new
 * identity -- and the renderer, quite reasonably, re-renders all of them. On
 * the desktop's WebKit that is a visible full-list repaint about a second
 * after marking one message read, because the client's own edit comes back as
 * a delta and the delta refetches the row.
 *
 * So each incoming row that is *the same message saying the same things* keeps
 * the object the list already holds, and a publish in which every row was kept
 * in the same order does not touch the signal at all. The comparison is
 * structural rather than by reference because the fresh copy has usually just
 * been fetched -- equal content under a different identity is the whole
 * situation this exists to detect.
 */
function publish(merged: Envelope[]): void {
  const prev = rows.peek();
  const byKey = new Map(prev.map((r) => [keyOf(r), r]));
  const next = merged.map((row) => {
    const old = byKey.get(keyOf(row));
    return old && sameJson(old.email, row.email) ? old : row;
  });
  if (next.length === prev.length && next.every((row, i) => row === prev[i])) return;
  rows.value = next;
}

export function allExhausted(): boolean {
  const list = [...cursors.values()];
  return list.length > 0 && list.every((c) => c.exhausted);
}

export async function reload({ quiet = false }: { quiet?: boolean } = {}): Promise<void> {
  if (!session.value) return;
  const saved = scope.value.kind === "saved";
  const remindersView = scope.value.kind === "reminders";
  // Held for the whole of a saved reload, including across the two awaits, so
  // the effect at the foot of this file does not start a second one on hearing
  // the pinned set that this reload itself just asked Rust for.
  if (saved) savedReloading = true;
  // Captured before the cursors are thrown away: how far the reader has paged
  // into each account, so a quiet refresh can come back with the same depth
  // instead of yanking the list up to the first forty rows.
  const depth = quiet ? depths() : undefined;
  // The reminders list is the server's answer; a reload somebody asked for
  // begins by asking for it again, as the saved view asks Rust below.
  if (remindersView && !quiet) await reminders.refresh();
  // Which messages are pinned is the local replica's answer, not the server's,
  // so "ask again" has to begin by asking Rust again -- a `Mailbox/get` says
  // nothing about it. Only on a reload somebody asked for: `app.tsx` reads the
  // set once at startup and gives the reason polling it would be wrong, and a
  // quiet reload is the poll it meant.
  if (saved && !quiet) await retention.load();
  reloading = true;
  cursors = new Map(
    targets().map((id) => [id, { position: 0, exhausted: false, buffer: [], total: undefined }]),
  );
  // The list about to be built is a new one, so whatever state the old one was
  // read at no longer describes it. Dropped here rather than overwritten in
  // `fetchPage`, so that the state recorded is the one belonging to the *first*
  // page: a later page is fetched at a later state, and adopting it would mean
  // claiming to have seen changes to the rows already on screen.
  for (const id of cursors.keys()) listStates.delete(id);
  batch(() => {
    if (!quiet) {
      rows.value = [];
      snippets.value = new Map();
      matchTotal.value = undefined;
      // A list someone asked for from scratch is the list the filters describe,
      // with no exceptions carried over from the last one.
      if (reprieved.value.size > 0) reprieved.value = new Map();
    }
    listError.value = null;
    partialErrors.value = new Map();
    loading.value = !quiet;
  });
  try {
    if (saved) await fetchSaved();
    else if (remindersView) await fetchReminders();
    else await fetchPage(depth);
  } finally {
    reloading = false;
    loading.value = false;
    // Only if this reload is the one that raised it. A reload of some other
    // view finishing first -- the reader changed their mind mid-fetch -- must
    // not hand the watcher back to a saved reload that is still in flight.
    if (saved) savedReloading = false;
  }
}

/** Rows currently held per account, clamped to what one request may return. */
function depths(): Map<string, number> {
  const out = new Map<string, number>();
  for (const [id, cursor] of cursors) {
    out.set(id, Math.min(MAX_REFETCH, Math.max(PAGE, cursor.buffer.length)));
  }
  return out;
}

export async function loadMore(): Promise<void> {
  // `fetching` and not just `loading`, and the difference is the whole bug.
  // `loading` is the spinner -- `reload` sets it to `!quiet`, because a refresh
  // the reader did not ask for must not flash one -- so during every quiet
  // reload this guard used to see an idle list and start paging into buffers
  // that were mid-rebuild. See `fetchPage`.
  if (loading.value || loadingMore.value || fetching > 0 || allExhausted()) return;
  loadingMore.value = true;
  try {
    await fetchPage();
  } finally {
    loadingMore.value = false;
  }
}

/**
 * Whether the list moved on while a fetch was in the air.
 *
 * Two reloads overlapping used to double every row on screen. `reload` replaces
 * `cursors` with fresh empty buffers and then awaits a fetch, so a startup load
 * and the quiet reload `lib/delta.ts` fires on a change could both be in flight
 * at once; each appended its page into whichever buffers were current when it
 * *returned*, and the later map collected both. 118 messages, 236 rows, each
 * one twice, and the same message id on both -- found on a phone, where the
 * extra layout pass of a scaled viewport was enough to widen the overlap.
 *
 * Identity rather than a counter, because the map already is the generation:
 * the buffers a fetch meant to fill either are still the current ones or have
 * been thrown away, and there is no third case to encode.
 *
 * The stale fetch is discarded rather than merged. Its rows are the same rows
 * the winning fetch asked for, one round trip older.
 */
function stale(generation: typeof cursors): boolean {
  return cursors !== generation;
}

/**
 * True from the moment a reload empties the buffers until its fetch refills
 * them.
 *
 * In that window the buffers do not describe the list on screen -- they describe
 * nothing at all -- so anything that builds a list *from* them is building it
 * from an empty set. {@link reload} knows this and leaves `rows` alone on a
 * quiet pass for exactly that reason; the mutation path did not, and published
 * a merge anyway.
 *
 * What that looked like: swipe a message, and the whole list vanished for about
 * a second and came back. Captured on the phone three times, always the same
 * shape -- 66 rows to 1, no skeleton rows, no spinner. The single survivor is
 * the tell. `write` inserts a patched message into its account's buffer when it
 * cannot find it there, that buffer had just been emptied by a quiet reload, and
 * a cursor holding one message sets the merge cutoff to that message's date --
 * so every row older than the one just swiped was held back. The absent skeleton
 * is what says it was not a loud reload, and the DOM row count falling is what
 * says it was not a repaint.
 *
 * Distinct from `fetching`, which counts *any* fetch: a `loadMore` is in flight
 * over buffers that are full and correct, and suppressing the optimistic update
 * for one of those would be a different bug in the other direction.
 */
let reloading = false;

/**
 * How many fetches are in the air, on any generation.
 *
 * The companion to {@link stale}, covering the case identity cannot. `stale`
 * asks "does this answer belong to the list on screen", which catches a fetch
 * left over from a *previous* list and is why two overlapping reloads no longer
 * double every row. It is blind to two fetches belonging to the **same** list,
 * and that is reachable: `reload` installs empty buffers and then awaits, so a
 * `loadMore` starting inside that await asks for page zero from a cursor whose
 * `position` the reload's own fetch has not come back to advance yet. Both
 * answers are current, both are appended, and the page lands twice.
 *
 * A count rather than a flag because `reload` and paging can legitimately nest
 * around each other, and a flag would be cleared by whichever finished first
 * while the other was still out.
 */
let fetching = 0;

async function fetchPage(limits?: Map<string, number>): Promise<void> {
  // The list this fetch belongs to. `reload` builds a whole new `cursors` map,
  // so its identity is the generation marker -- see `stale`.
  const generation = cursors;
  const pending = [...cursors.entries()].filter(([, c]) => !c.exhausted);
  if (pending.length === 0) return;
  fetching += 1;
  try {
    await fetchInto(generation, pending, limits);
  } finally {
    fetching -= 1;
  }
}

/** The body of a fetch, split out only so `fetching` can bracket all of it. */
async function fetchInto(
  generation: typeof cursors,
  pending: [string, Cursor][],
  limits?: Map<string, number>,
): Promise<void> {
  const limitFor = (id: string): number => limits?.get(id) ?? PAGE;
  const specs = pending.map(([id, c]) => specFor(id, c.position, limitFor(id)));
  let pages;
  try {
    pages = await jmap.queryPages(specs);
  } catch (e) {
    if (stale(generation)) return;
    listError.value = message(e);
    report(`reload: whole fetch failed: ${message(e)}`);
    return;
  }
  if (stale(generation)) {
    report("reload: answer discarded, a newer list replaced this one mid-fetch");
    return;
  }

  const failures = new Map<string, string>();
  for (const page of pages) {
    const cursor = cursors.get(page.accountId);
    if (!cursor) continue;
    if (page.error) {
      failures.set(page.accountId, page.error);
      // Stop paging an account that is answering with errors, so "load more"
      // does not retry it forever on every scroll.
      cursor.exhausted = true;
      continue;
    }
    // The buffer's newest-first invariant is this module's to hold, not the
    // server's to grant: `merge`'s cutoff, `oldestHeld` and `insertionPoint`
    // all read buffer *positions* as dates. It was taken on trust from the
    // `Email/get` resolving the query's ids -- whose order RFC 8620 leaves
    // undefined, and which this daemon answered in row-id order until it was
    // caught. One shuffled page put a recent date in the last slot, the merge
    // cutoff landed on it, and eight accounts' full pages collapsed to a
    // single row on screen. Sorted here, where the invariant lives.
    const emails = [...page.emails].sort(
      (a, b) => Date.parse(b.receivedAt) - Date.parse(a.receivedAt),
    );
    cursor.buffer = [...cursor.buffer, ...emails];
    cursor.position += page.emails.length;
    cursor.total = page.total;
    // See `reload`: the first page's state is the one that describes this list.
    if (page.queryState && !listStates.has(page.accountId)) {
      listStates.set(page.accountId, page.queryState);
    }
    if (page.emails.length < limitFor(page.accountId)) cursor.exhausted = true;
  }

  const merged = merge();
  batch(() => {
    publish(merged);
    matchTotal.value = countMatches();
    partialErrors.value = failures;
    pruneSelection();
  });

  // The whole story of one fetch in one line: what each account answered, and
  // what the merge made of it. When the list is wrong, this is the fork between
  // "the server sent nothing", "an account refused", and "the client threw it
  // away" -- the three cases that look identical from the outside.
  report(
    `reload: ${pages
      .map((p) => `${p.accountId}=${p.error ? `error(${p.error})` : p.emails.length}`)
      .join(" ")} -> ${merged.length} rows, ${rows.peek().length} on screen`,
  );

  const text = search.value.trim();
  if (text) void loadSnippets(text, merged);
}

// ---- the kept-offline list ----------------------------------------------------

/** True while {@link reload} is already rebuilding the kept-offline list. */
let savedReloading = false;

/**
 * The kept-offline list: one `Email/get` per account, and no query at all.
 *
 * This is a deliberately separate path rather than a filter bolted onto
 * {@link fetchPage}, and the reason is that a pin is not a property of the
 * mail. It lives in this device's replica, the daemon has never been told about
 * it, and there is no `FilterCondition` that would select these messages --
 * so pretending otherwise would mean fetching a page of *something else* and
 * discarding most of it, which is both slower and wrong at the boundary of
 * every page. The ids are already known; the only thing missing is the rows.
 *
 * **No paging, on purpose.** A pin is a promise somebody makes one message at a
 * time, so the set is a handful and not a cache. `Email/get` names all of them
 * at once, every cursor is exhausted the moment it is filled, and the list foot
 * never asks for more -- which is why there is no `IntersectionObserver` and no
 * position to carry. The chunking in `jmap.getEmails` is the only concession,
 * and it is there because "small" is a policy in Rust rather than a bound this
 * side can rely on.
 *
 * **The filters and the search are applied here, locally.** `lib/filters.ts`
 * argues at length that a local predicate over a *paged* list is wrong, and it
 * is: it would count the forty rows in hand and call it the whole account. That
 * argument does not reach this view, because the rows in hand *are* the whole
 * population -- there is nothing behind them for a later page to contradict. So
 * the honest choice is between filtering here and having the chips do nothing
 * at all in this one view, and a control that silently does nothing is the
 * failure this feature is built around avoiding.
 *
 * The search is the one place that costs something. A server search reads the
 * bodies; this one can only see the fields on the row, so it is narrower than
 * the same words typed in any other view. Narrower and honest beats a search
 * box that ignores what is typed into it.
 */
async function fetchSaved(): Promise<void> {
  const generation = cursors;
  // Read once for the whole pass rather than per account: it carries the
  // server's `maxObjectsInGet`, and a limit that changed halfway through would
  // chunk two accounts' requests differently for no reason.
  const current = session.peek();
  if (!current) return;
  const wanted = pinnedByAccount();
  const active = filters.filters.value;
  const text = search.value.trim().toLowerCase();
  const failures = new Map<string, string>();

  await Promise.all(
    [...wanted].map(async ([accountId, ids]) => {
      const cursor = cursors.get(accountId);
      if (!cursor) return;
      // Exhausted whatever happens, including on failure: there is no second
      // page for "load more" to go and ask for, and an account that could not
      // answer must not be retried on every scroll.
      cursor.exhausted = true;
      try {
        const got = await jmap.getEmails(current, accountId, ids);
        cursor.buffer = got
          .filter((email) => filters.matches(email, active) && mentions(email, text))
          // Newest first, as a query would have returned them: `merge` sorts its
          // own output, but `oldestHeld` and `insertionPoint` read the buffer
          // directly and both take the order on trust.
          .sort((a, b) => Date.parse(b.receivedAt) - Date.parse(a.receivedAt));
        cursor.total = cursor.buffer.length;
      } catch (e) {
        failures.set(accountId, message(e));
      }
    }),
  );

  if (stale(generation)) return;

  const merged = merge();
  batch(() => {
    publish(merged);
    matchTotal.value = countMatches();
    partialErrors.value = failures;
    pruneSelection();
  });
}

/**
 * The reminders view: the messages named by `lib/reminders.ts`, fetched by id
 * and ordered by when they are due, soonest first -- which puts the overdue
 * ones on top, since their time is in the past. One request per account, no
 * paging, exhausted at once, exactly as {@link fetchSaved}.
 */
async function fetchReminders(): Promise<void> {
  const generation = cursors;
  const current = session.peek();
  if (!current) return;
  const wanted = remindersByAccount();
  const active = filters.filters.value;
  const text = search.value.trim().toLowerCase();
  const failures = new Map<string, string>();
  const due = reminders.byKey.peek();

  await Promise.all(
    [...wanted].map(async ([accountId, ids]) => {
      const cursor = cursors.get(accountId);
      if (!cursor) return;
      cursor.exhausted = true;
      try {
        const got = await jmap.getEmails(current, accountId, ids);
        cursor.buffer = got
          .filter((email) => filters.matches(email, active) && mentions(email, text))
          .sort((a, b) => {
            const ra = due.get(reminders.keyOf(accountId, a.id))?.remindAt ?? 0;
            const rb = due.get(reminders.keyOf(accountId, b.id))?.remindAt ?? 0;
            return ra - rb;
          });
        cursor.total = cursor.buffer.length;
      } catch (e) {
        failures.set(accountId, message(e));
      }
    }),
  );

  if (stale(generation)) return;

  // By when each is due, soonest first, across accounts: `merge` orders by
  // arrival, which is the wrong axis for a to-do list, and the overdue ones
  // belong at the top.
  const merged = merge().sort(
    (a, b) =>
      (due.get(reminders.keyOf(a.accountId, a.email.id))?.remindAt ?? 0) -
      (due.get(reminders.keyOf(b.accountId, b.email.id))?.remindAt ?? 0),
  );
  batch(() => {
    publish(merged);
    matchTotal.value = countMatches();
    partialErrors.value = failures;
    pruneSelection();
  });
}

/**
 * Re-read the reminders list and, when it is the view on screen, the view.
 * Called after every stream event and after every change made here, so the
 * section is never a stale copy of what the daemon holds.
 */
export async function refreshReminders(): Promise<void> {
  await reminders.refresh();
  if (scope.peek().kind === "reminders" && session.peek()) await reload({ quiet: true });
}

/**
 * Whether a row's own text contains what was typed, for the one view that
 * cannot ask the server.
 *
 * Subject, preview and the addresses, because those are the fields the row
 * already carries -- fetching bodies to search them would undo the whole reason
 * this view is one cheap request. `preview` is the opening of the body, so this
 * is not purely a header search, but it is not a full-text one either and
 * nothing here should claim it is.
 */
function mentions(email: Email, text: string): boolean {
  if (!text) return true;
  const names = [...(email.from ?? []), ...(email.to ?? [])]
    .map((a) => `${a.name ?? ""} ${a.email}`)
    .join(" ");
  return `${email.subject ?? ""} ${email.preview} ${names}`.toLowerCase().includes(text);
}

/**
 * Follow the pinned set for as long as this session lasts.
 *
 * Unpinning from the reading pane while the kept-offline list is on screen has
 * to take the row with it, or the view is asserting something about this device
 * that has just stopped being true. Nothing else can do it: `setPinned` writes
 * `retention.pinned` and never goes near `editLocally`, so no patch reaches the
 * buffers at all.
 *
 * Started on the first visit rather than at module scope, and the note on the
 * `retention` import says why that is not an optimisation. Never stopped: what
 * it costs afterwards is one comparison per pin, and a watcher that had to be
 * torn down on the way out of the view would be a second thing to remember.
 */
let pinWatch: (() => void) | null = null;

function watchPins(): void {
  if (pinWatch) return;
  let subscribing = true;
  pinWatch = effect(() => {
    // Read first and unconditionally, or the early returns below would
    // unsubscribe the effect from the one signal it exists to follow.
    retention.pinned.value;
    // The run that makes the subscription is not news about anything.
    if (subscribing) {
      subscribing = false;
      return;
    }
    if (scope.peek().kind !== "saved") return;
    // A reload that is already under way asked Rust for this set itself.
    if (savedReloading) return;
    // Quiet: a pin going is one row leaving, not a reason to blank the list
    // under somebody who is reading the message they have just unpinned.
    void reload({ quiet: true });
  });
}

/**
 * The totals every account reported, added up.
 *
 * One account that could not answer makes the sum meaningless rather than
 * merely smaller, so it comes back `undefined` and the UI says nothing instead
 * of showing a number the reader would take to be the whole picture.
 */
function countMatches(): number | undefined {
  let sum = 0;
  for (const [, cursor] of cursors) {
    if (cursor.total === undefined) return undefined;
    sum += cursor.total;
  }
  return sum;
}

async function loadSnippets(text: string, merged: Envelope[]): Promise<void> {
  const byAccount = new Map<string, string[]>();
  for (const row of merged) {
    const list = byAccount.get(row.accountId) ?? [];
    list.push(row.email.id);
    byAccount.set(row.accountId, list);
  }
  const found = new Map(snippets.value);
  for (const [accountId, ids] of byAccount) {
    const got = await jmap.getSnippets(accountId, text, ids.slice(0, 100));
    for (const [id, snippet] of got) found.set(`${accountId}:${id}`, snippet);
  }
  snippets.value = found;
}

// ---- the multi-selection -----------------------------------------------------

/**
 * The selected messages, held as identities rather than as positions.
 *
 * This is the whole reason the type is a `Map` keyed by `accountId:id` and not
 * a list of row numbers. The list under it is not stable: the server says
 * something changed and a refresh runs, new mail arrives at the top, and a
 * merged view reorders whenever one account answers before another. It is less
 * stable than it was, not more -- a refresh now lands seconds after a change
 * rather than up to a minute later. A selection stored as indices would quietly
 * come to mean different messages, and someone who ticked forty rows and then
 * pressed Archive would archive forty others. Identity is the only thing about
 * a row that survives a refresh.
 *
 * The envelope is kept beside the key because an action needs the message's
 * current mailboxes and keywords, and a selected message is not always in
 * `rows`: it can be held back by the merge cutoff, or sit deeper than the
 * refresh re-read. Losing the selection because a row scrolled out of the
 * buffer would be its own bug.
 */
export const selection = signal<Map<string, Envelope>>(new Map());

/** Where a shift-click range measures from. Not the same as the open message. */
export const anchor = signal<string | null>(null);

/** The row the keyboard is on, so `x` has something to tick. */
export const cursor = signal<string | null>(null);

export const selectionCount = computed(() => selection.value.size);

export function keyOf(row: Envelope): string {
  return `${row.accountId}:${row.email.id}`;
}

export function isSelected(key: string): boolean {
  return selection.value.has(key);
}

/**
 * The selected messages, each as fresh as the list can make it.
 *
 * The copy in `selection` is a snapshot from whenever it was ticked; the copy
 * in `rows` has whatever has happened since. An action wants the latter, since
 * it decides which mailbox a message is leaving.
 */
export function selectedRows(): Envelope[] {
  const fresh = new Map(rows.value.map((r) => [keyOf(r), r]));
  return [...selection.value].map(([key, row]) => fresh.get(key) ?? row);
}

export function toggleSelect(row: Envelope): void {
  const key = keyOf(row);
  const next = new Map(selection.value);
  if (next.has(key)) next.delete(key);
  else next.set(key, row);
  batch(() => {
    selection.value = next;
    anchor.value = key;
    cursor.value = key;
  });
}

/**
 * Extend the selection from the anchor to this row, inclusive.
 *
 * Range over the list as it is drawn, not over ids, because "everything between
 * these two" is a statement about what the reader can see. With no anchor yet
 * this degrades to selecting the one row, which is what a shift-click into an
 * empty selection means to everyone.
 */
export function extendSelection(row: Envelope): void {
  const key = keyOf(row);
  const list = rows.value;
  const from = list.findIndex((r) => keyOf(r) === anchor.value);
  const to = list.findIndex((r) => keyOf(r) === key);
  if (to < 0 || from < 0) {
    toggleSelect(row);
    return;
  }
  const next = new Map(selection.value);
  for (let i = Math.min(from, to); i <= Math.max(from, to); i += 1) {
    const r = list[i];
    if (r) next.set(keyOf(r), r);
  }
  batch(() => {
    selection.value = next;
    cursor.value = key;
  });
}

export function clearSelection(): void {
  if (selection.value.size === 0 && anchor.value === null) return;
  batch(() => {
    selection.value = new Map();
    anchor.value = null;
  });
}

/**
 * How many messages the select-all box ticks.
 *
 * Not every message in the view, deliberately. "Select all" followed by Delete
 * is the shortest path in the app to destroying a mailbox, and it is two clicks
 * that are easy to make by accident -- the box sits in the header the pointer
 * crosses on the way to everything else. Capped, the worst a slip can do is
 * twenty messages, a number someone can read back in the bar and recover from.
 * Clearing a big folder is still quick: act on twenty, and the next twenty move
 * up to be ticked.
 *
 * A shift-click range is not capped. It is aimed, at two rows the reader chose.
 */
export const SELECT_ALL_LIMIT = 20;

/** The rows the select-all box means: the top of the list as it is drawn. */
function selectAllRows(): Envelope[] {
  return rows.value.slice(0, SELECT_ALL_LIMIT);
}

/**
 * What the select-all box shows. `"all"` is exactly the rows it would tick --
 * any other non-empty selection is `"some"`, so the box only reads as checked
 * when clicking it again would clear precisely what is ticked.
 */
export const selectAllState = computed<"none" | "some" | "all">(() => {
  const picked = selection.value;
  if (picked.size === 0) return "none";
  const head = selectAllRows();
  return head.length === picked.size && head.every((r) => picked.has(keyOf(r))) ? "all" : "some";
});

/**
 * The first {@link SELECT_ALL_LIMIT} rows, or none if they are already it.
 *
 * Replacing the selection rather than adding to it, so the box can never leave
 * more than the limit ticked however it is pressed -- including from a partial
 * selection, which is the only way to reach it on a phone, where the bar holding
 * it appears after a long press has already ticked one row.
 */
export function toggleSelectAll(): void {
  if (selectAllState.value === "all") {
    clearSelection();
    return;
  }
  const head = selectAllRows();
  if (head.length === 0) return;
  selection.value = new Map(head.map((r) => [keyOf(r), r]));
}

/**
 * Reconcile the selection with what the server just said exists.
 *
 * The dangerous half of "the selection survives a refresh" is the other half:
 * it must let go of messages that are genuinely gone, or a bulk action sends
 * ids the server will only reject. But absence from a fresh page is not proof
 * of removal -- a message can simply be older than the depth this refresh
 * re-read, since a quiet refresh re-fetches what is on screen and no more.
 *
 * So the test is the boundary, not membership: each account reports the oldest
 * message it just returned, and a selected message newer than that boundary
 * which did not come back has really left the mailbox. Anything older is out of
 * the window and keeps its place. Using the timestamp rather than the row
 * number is what makes this immune to mail arriving at the top mid-refresh.
 */
function pruneSelection(): void {
  const current = selection.value;
  if (current.size === 0) return;

  const present = new Map<string, Envelope>();
  const floor = new Map<string, number>();
  for (const [accountId, cursorState] of cursors) {
    for (const email of cursorState.buffer) present.set(`${accountId}:${email.id}`, { accountId, email });
    const oldest = cursorState.buffer[cursorState.buffer.length - 1];
    floor.set(
      accountId,
      cursorState.exhausted || !oldest ? -Infinity : Date.parse(oldest.receivedAt),
    );
  }

  const next = new Map<string, Envelope>();
  for (const [key, row] of current) {
    const fresh = present.get(key);
    if (fresh) {
      next.set(key, fresh);
      continue;
    }
    const boundary = floor.get(row.accountId);
    // An account this refresh did not query at all cannot testify about it.
    if (boundary === undefined || Date.parse(row.email.receivedAt) < boundary) next.set(key, row);
  }
  if (next.size !== current.size) selection.value = next;
}

// ---- editing the local copy --------------------------------------------------

/** Apply one JMAP patch to a message, returning a new object. */
function patched(email: Email, patch: EmailPatch): Email {
  const keywords = { ...email.keywords };
  const mailboxIds = { ...email.mailboxIds };
  for (const [path, value] of Object.entries(patch)) {
    const slash = path.indexOf("/");
    if (slash < 0) continue;
    const field = path.slice(0, slash);
    const name = path.slice(slash + 1);
    const bag = field === "keywords" ? keywords : field === "mailboxIds" ? mailboxIds : null;
    if (!bag) continue;
    if (value === null) delete bag[name];
    else bag[name] = true;
  }
  return { ...email, keywords, mailboxIds };
}

/**
 * Whether a message still belongs in the list on screen.
 *
 * This is what makes an archive look like an archive: the row leaves because
 * the mailbox this view is showing is no longer one of the message's. A search
 * is not a folder and holds nothing back.
 *
 * An account with no mailbox for the role is not in the view -- `targets` does
 * not query it -- so a message of its cannot belong to this list. That used to
 * read `true` here, on the theory that such an account was showing nothing and
 * so had nothing to lose, which was the same wrong assumption that let the
 * query return its entire archive.
 *
 * The filters are deliberately not part of this. Moving a message out of the
 * mailbox someone is looking at is what they asked for and the row should go;
 * marking as read a message they are reading is also what they asked for and
 * the row should stay. Both are `Email/set` calls that arrive here identically,
 * and the difference is entirely about what the reader expects to see happen.
 * `reprieved` is where the second case lands.
 *
 * Starred and Saved answer `true` to everything for the same reason a
 * search does: neither is a folder, so no move can take a message out of one.
 * Archiving a starred message leaves it starred and archiving a pinned one
 * leaves it on the disk, and a row that vanished on either would be the client
 * claiming something it has no basis for. Unstarring *does* leave the starred
 * view, but that is the reader making a row stop matching while they are
 * looking at it -- the `reprieved` case -- and it is handled below.
 */
function inScope(accountId: string, email: Email): boolean {
  const s = scope.value;
  if (search.value.trim()) return true;
  if (s.kind === "starred" || s.kind === "saved" || s.kind === "reminders") return true;
  if (s.kind === "mailbox") return accountId === s.accountId && email.mailboxIds[s.mailboxId] === true;
  const id = roleMailbox(accountId, s.role);
  return id ? email.mailboxIds[id] === true : false;
}

/**
 * In scope, but no longer what the list was asked for.
 *
 * The three client-side mirrors of conditions `specFor` compiles into the
 * server query: the filter chips, the inbox tab, and the Starred view's
 * keyword. A message failing any of them would not come back if the list were
 * asked for again, and the two places that edit rows without asking -- the
 * optimistic edit and the delta echo -- both need that fact to decide between
 * keeping a row, reprieving it, and letting it go.
 */
function unmatched(email: Email): boolean {
  return (
    (filters.anyActive.peek() && !filters.matches(email, filters.filters.peek())) ||
    (hasBuckets.peek() && priority.bucketOf(email) !== priority.bucket.peek()) ||
    (scope.peek().kind === "starred" && !email.keywords["$flagged"])
  );
}

/** One mailbox's counters, as a change moves them. */
interface CountDelta {
  total: number;
  unread: number;
  lowTotal: number;
  lowUnread: number;
}

/**
 * Sidebar counters, adjusted for one message changing shape.
 *
 * The low-priority halves move with the JMAP ones. The focused count is
 * `unreadEmails - lowPriorityUnreadEmails` (`unreadTotal`), so reading a
 * low-priority message and adjusting only `unreadEmails` took one off the
 * Inbox badge while the Low priority badge kept it, until a refresh brought
 * the daemon's numbers back. "Low" is the tab the message shows in
 * (`priority.bucketOf`), which is what the daemon's counters count: a flagged
 * message is focused.
 */
function countDelta(deltas: Map<string, CountDelta>, before: Email | null, after: Email | null): void {
  const add = (email: Email | null, sign: number) => {
    if (!email) return;
    const unread = email.keywords["$seen"] ? 0 : 1;
    const low = priority.bucketOf(email) === "low" ? 1 : 0;
    for (const [id, present] of Object.entries(email.mailboxIds)) {
      if (!present) continue;
      const d = deltas.get(id) ?? { total: 0, unread: 0, lowTotal: 0, lowUnread: 0 };
      d.total += sign;
      d.unread += sign * unread;
      d.lowTotal += sign * low;
      d.lowUnread += sign * low * unread;
      deltas.set(id, d);
    }
  };
  add(before, -1);
  add(after, 1);
}

function applyCounts(accountId: string, deltas: Map<string, CountDelta>): void {
  if (deltas.size === 0) return;
  const list = mailboxes.value.get(accountId);
  if (!list) return;
  const next = new Map(mailboxes.value);
  next.set(
    accountId,
    list.map((m) => {
      const d = deltas.get(m.id);
      if (!d) return m;
      const out = {
        ...m,
        totalEmails: Math.max(0, m.totalEmails + d.total),
        unreadEmails: Math.max(0, m.unreadEmails + d.unread),
      };
      // Absent stays absent: a daemon without the split reads as "none".
      if (m.lowPriorityEmails !== undefined) {
        out.lowPriorityEmails = Math.max(0, m.lowPriorityEmails + d.lowTotal);
      }
      if (m.lowPriorityUnreadEmails !== undefined) {
        out.lowPriorityUnreadEmails = Math.max(0, m.lowPriorityUnreadEmails + d.lowUnread);
      }
      return out;
    }),
  );
  mailboxes.value = next;
}

interface Restored {
  accountId: string;
  before: Email | null;
  after: Email | null;
  /** True if the change took the row out of the list. */
  dropped: boolean;
}

/** Where a message goes back into a buffer sorted newest-first. */
function insertionPoint(buffer: Email[], email: Email): number {
  const at = Date.parse(email.receivedAt);
  const i = buffer.findIndex((e) => Date.parse(e.receivedAt) < at);
  return i < 0 ? buffer.length : i;
}

/**
 * Change the local copy of some messages now, and hand back the undo.
 *
 * A message lives in two places here -- the merged `rows` signal and the
 * per-account buffer behind it -- and editing only the first is how an archived
 * message reappears the moment someone scrolls far enough to fetch the next
 * page. Both are updated, along with the sidebar counters, which otherwise
 * carry the old unread total until a refresh lands -- a round trip after the
 * user watched forty messages leave, and longer than that if the change was
 * theirs and the server has nothing new to announce.
 *
 * `cursors[a].position` comes down with a removed row on purpose. It is an
 * offset into the server's result set, and the server's result set lost the
 * same row, so leaving it alone would make the next page skip one.
 *
 * The undo takes the subset of keys to put back, because the case to design
 * for is not "the request failed" but "thirty-nine of forty were accepted".
 * Restoring by key rather than by restoring a snapshot of the whole buffer is
 * deliberate too: several seconds pass before the answer arrives, and a refresh
 * in that window must not be clobbered by a stale copy of the list.
 *
 * A `null` patch means the message was destroyed.
 *
 * `bases` supplies a copy of a message this view does not currently hold, and
 * exists for exactly one caller: Undo. An archived message is no longer in any
 * buffer -- that is what archiving it did -- so putting it back has to start
 * from a copy the caller kept rather than from one that is here. Without it the
 * undo reaches the server, the message really does come back, and the list goes
 * on showing it as gone until something else refreshes it.
 */
export function editLocally(
  edits: Map<string, EmailPatch | null>,
  bases?: Map<string, Email>,
): (keys?: string[]) => void {
  const record = new Map<string, Restored>();

  const write = (
    entries: [string, EmailPatch | null][],
    reverse: boolean,
  ): void => {
    const byAccount = new Map<string, Map<string, { before: Email | null; after: Email | null }>>();
    const dropped: string[] = [];
    for (const [key, patch] of entries) {
      const at = key.indexOf(":");
      const accountId = key.slice(0, at);
      const id = key.slice(at + 1);
      const cursorState = cursors.get(accountId);
      if (!cursorState) continue;

      let before: Email | null;
      let after: Email | null;
      if (reverse) {
        const prior = record.get(key);
        if (!prior) continue;
        before = cursorState.buffer.find((e) => e.id === id) ?? prior.after;
        after = prior.before;
      } else {
        const live =
          cursorState.buffer.find((e) => e.id === id) ??
          selection.value.get(key)?.email ??
          bases?.get(key);
        if (!live) continue;
        before = live;
        after = patch === null ? null : patched(live, patch);
      }

      const kept = after !== null && inScope(accountId, after) ? after : null;
      // Kept, but no longer what the reader asked to see. Recorded rather than
      // removed, and recorded on the way back out too: undoing a "mark as read"
      // under an unread filter makes the row ordinary again, and leaving the
      // "no longer matches" note on it would be a lie about live state.
      // Flagging a message in the low-priority tab takes it out of that tab --
      // that is what the star means -- and the row must stay put and say so
      // rather than vanish under the cursor. Exactly the case `reprieved`
      // exists for, so the bucket joins the filters in deciding staleness
      // rather than joining `inScope`, which would drop the row.
      // Unstarring in the Starred view is the same shape a third time, and it
      // is the one somebody will meet most: pressing `s` on a message there is
      // how a starred list gets worked through, and a row that disappeared on
      // each press would take the reader's place in the list with it -- along
      // with the only way back, since Undo is on the row that just left. So it
      // stays, marked, until the list is next asked for from scratch.
      const stale = kept !== null && unmatched(kept);
      if (stale) {
        reprieved.value = new Map(reprieved.peek()).set(key, { accountId, email: kept });
      } else if (reprieved.peek().has(key)) {
        // Matching again, or gone from the list entirely. Either way it is no
        // longer an exception the reader needs told about.
        const next = new Map(reprieved.peek());
        next.delete(key);
        reprieved.value = next;
      }
      const index = cursorState.buffer.findIndex((e) => e.id === id);
      if (kept === null) {
        if (index >= 0) {
          cursorState.buffer.splice(index, 1);
          cursorState.position = Math.max(0, cursorState.position - 1);
        }
      } else if (index >= 0) {
        cursorState.buffer[index] = kept;
      } else {
        cursorState.buffer.splice(insertionPoint(cursorState.buffer, kept), 0, kept);
        cursorState.position += 1;
      }

      if (!reverse) {
        record.set(key, { accountId, before, after, dropped: kept === null });
        if (kept === null) dropped.push(key);
      }
      const bucket = byAccount.get(accountId) ?? new Map();
      bucket.set(key, { before, after });
      byAccount.set(accountId, bucket);
    }

    batch(() => {
      for (const [accountId, bucket] of byAccount) {
        const deltas = new Map<string, CountDelta>();
        for (const { before, after } of bucket.values()) countDelta(deltas, before, after);
        applyCounts(accountId, deltas);
      }
      // The selection follows the message, not the row: a change that takes it
      // out of this view has not unticked it, and the copy held there must be
      // the new one or the next action would send a stale mailbox id.
      const nextSelection = new Map(selection.value);
      let touched = false;
      for (const [accountId, bucket] of byAccount) {
        for (const [key, { after }] of bucket) {
          if (!nextSelection.has(key)) continue;
          touched = true;
          if (after === null) nextSelection.delete(key);
          else nextSelection.set(key, { accountId, email: after });
        }
      }
      if (touched) selection.value = nextSelection;

      for (const [accountId, bucket] of byAccount) {
        for (const [key, { after }] of bucket) {
          if (openEmailId.value !== key) continue;
          // Keep the body: the patch only ever touches keywords and mailboxes,
          // and the loaded copy in the reader has parts the list rows do not.
          if (after !== null && openEmail.value) {
            openEmail.value = {
              accountId,
              email: { ...openEmail.value.email, keywords: after.keywords, mailboxIds: after.mailboxIds },
            };
          }
        }
      }
      // Before `rows`, so "the next one" is read off the ordering the reader was
      // actually looking at rather than the one that is about to replace it.
      advancePast(dropped);
      // Not while a reload holds the buffers. See `reloading`: merging now
      // would publish a list built from empty buffers, which is how one swipe
      // blanked sixty-five unrelated rows. The reload's own fetch publishes the
      // list a moment later, with this change already in it -- the patch is not
      // lost, only its premature drawing.
      if (!reloading) publish(merge());
    });
  };

  write([...edits], false);

  return (keys?: string[]) => {
    const wanted = keys ?? [...record.keys()];
    write(
      wanted.filter((k) => record.has(k)).map((k) => [k, null] as [string, EmailPatch | null]),
      true,
    );
    for (const k of wanted) record.delete(k);
  };
}

/**
 * Take the server's fresh copies of messages this view already holds.
 *
 * The other half of `editLocally`, for changes arriving rather than leaving.
 * When a delta reports that the only movement in the view is to messages the
 * client is holding -- its own edit coming back round, or another device
 * touching a row on screen -- `lib/delta.ts` fetches those messages and lands
 * them here instead of reloading the list, because the reload rebuilt every
 * row to change one and the whole list visibly repainted a second after each
 * click.
 *
 * The outcome per message is decided the way a reload would decide it: out of
 * scope or destroyed and the row goes; in scope but `unmatched` and the row
 * goes too, since the server would not have returned it -- *unless* the reader
 * put it in `reprieved`, in which case the fresh copy replaces the held one and
 * the row stays exactly as that mechanism promises. What this deliberately
 * does differently from `editLocally`:
 *
 *   - **No reprieving.** A reprieve is for a row the *reader* made stop
 *     matching. A change arriving from elsewhere is not that, and today's
 *     reload drops such a row; so does this.
 *   - **No sidebar counter deltas.** The `Mailbox/changes` half of the same
 *     poll carries the server's own numbers, and adding a delta on top of an
 *     absolute answer would count everything twice.
 *   - **No `advancePast`.** Moving the reader off a row is for rows their own
 *     action removed. A reload never yanked the open message when a row left
 *     underneath it, and neither does this.
 *   - **Writes only what changed.** Every copy -- buffer, selection, reprieve,
 *     the open message -- is compared before it is replaced, so the echo of an
 *     edit the client already applied is what it should be: nothing.
 *
 * `missing` is the ids the server did not return: destroyed messages, dropped
 * from everywhere they are held.
 */
export function absorb(accountId: string, fetched: Email[], missing: string[]): void {
  const cursorState = cursors.get(accountId);
  const changes: [string, Email | null][] = [
    ...fetched.map((e): [string, Email] => [`${accountId}:${e.id}`, e]),
    ...missing.map((id): [string, null] => [`${accountId}:${id}`, null]),
  ];
  batch(() => {
    let nextSelection: Map<string, Envelope> | null = null;
    let nextReprieved: Map<string, Envelope> | null = null;
    for (const [key, after] of changes) {
      const id = key.slice(accountId.length + 1);
      const kept = after !== null && inScope(accountId, after) ? after : null;
      const wanted = kept !== null && !unmatched(kept);
      const wasReprieved = (nextReprieved ?? reprieved.peek()).has(key);

      if (cursorState) {
        const index = cursorState.buffer.findIndex((e) => e.id === id);
        if (kept !== null && (wanted || wasReprieved)) {
          if (index >= 0) {
            if (!sameJson(cursorState.buffer[index], kept)) cursorState.buffer[index] = kept;
          } else if (wanted) {
            cursorState.buffer.splice(insertionPoint(cursorState.buffer, kept), 0, kept);
            cursorState.position += 1;
            if (cursorState.total !== undefined) cursorState.total += 1;
          }
        } else if (index >= 0) {
          cursorState.buffer.splice(index, 1);
          cursorState.position = Math.max(0, cursorState.position - 1);
          if (cursorState.total !== undefined) cursorState.total = Math.max(0, cursorState.total - 1);
        }
      }

      if (kept !== null && !wanted && wasReprieved) {
        const held = (nextReprieved ?? reprieved.peek()).get(key);
        if (!held || !sameJson(held.email, kept)) {
          nextReprieved = nextReprieved ?? new Map(reprieved.peek());
          nextReprieved.set(key, { accountId, email: kept });
        }
      } else if (wasReprieved && (kept === null || wanted)) {
        nextReprieved = nextReprieved ?? new Map(reprieved.peek());
        nextReprieved.delete(key);
      }

      // The selection follows the message, exactly as in `editLocally`.
      const selected = (nextSelection ?? selection.peek()).get(key);
      if (selected && (after === null || !sameJson(selected.email, after))) {
        nextSelection = nextSelection ?? new Map(selection.peek());
        if (after === null) nextSelection.delete(key);
        else nextSelection.set(key, { accountId, email: after });
      }

      const open = openEmail.peek();
      if (openEmailId.peek() === key && after !== null && open) {
        if (!sameJson(open.email.keywords, after.keywords) || !sameJson(open.email.mailboxIds, after.mailboxIds)) {
          openEmail.value = {
            accountId,
            email: { ...open.email, keywords: after.keywords, mailboxIds: after.mailboxIds },
          };
        }
      }
    }
    if (nextReprieved) reprieved.value = nextReprieved;
    if (nextSelection) selection.value = nextSelection;
    matchTotal.value = countMatches();
    if (!reloading) publish(merge());
  });
}

/**
 * Move the reader off messages that are leaving the list.
 *
 * Archiving from the reading pane and landing on an empty pane is how a
 * keyboard-driven pass through an inbox stops being keyboard-driven: every
 * `e` would have to be followed by a `j` to get anywhere.
 */
function advancePast(keys: string[]): void {
  const current = openEmailId.value;
  if (keys.length === 0 || !current || !keys.includes(current)) return;
  const list = rows.value;
  const leaving = new Set(keys);
  const at = list.findIndex((r) => keyOf(r) === current);
  const next = list.slice(at + 1).find((r) => !leaving.has(keyOf(r)));
  if (next) void open(next);
  else closeEmail();
}

// ---- scope and the open message ----------------------------------------------

export function setScope(next: Scope): void {
  batch(() => {
    scope.value = next;
    openEmail.value = null;
    openEmailId.value = null;
    cursor.value = null;
    // Cleared rather than pruned. A selection is an answer to "these ones, out
    // of what I am looking at", and the reader is no longer looking at that;
    // carrying ticks into a different mailbox would put the count somewhere
    // nobody can see what it refers to.
    clearSelection();
    if (view.value !== "mail") view.value = "mail";
  });
  // The kept-offline list is the only view whose membership can change without
  // anything being fetched, so it is the only one that needs a watcher. Armed
  // on the first visit; see `watchPins`.
  if (next.kind === "saved") watchPins();
  void reload();
}

/**
 * Switch inbox tabs.
 *
 * Scope-like rather than filter-like, and treated as such: the list is asked
 * for again from scratch and the selection is cleared, because forty ticks were
 * an answer to "these ones, out of what I am looking at" and the reader is no
 * longer looking at that. The open message is left alone -- switching tabs to
 * check the other one should not close what you were reading.
 */
export function setBucket(next: priority.Bucket): void {
  if (priority.bucket.value === next) return;
  priority.bucket.value = next;
  clearSelection();
  void reload();
}

/**
 * Reclassification happened on the server: throw away what is on screen and ask
 * again.
 *
 * A rule change is retroactive across the whole archive, so there is no patch
 * this client could apply to the rows it holds that would be right -- the
 * message it acted on is one of possibly hundreds that moved, in mailboxes it
 * has not paged into. Re-reading the mailbox counters matters as much as the
 * list: they are what the two tabs are labelled with.
 */
export async function reclassified(): Promise<void> {
  await refresh();
}

/**
 * Change a filter and go and ask again.
 *
 * These three wrappers exist so there is no way to move a filter without
 * re-running the query. `lib/filters.ts` owns what a filter means and could be
 * poked directly from a component; if anything ever did, the trigger's count and
 * the sentence would move and the list under them would keep showing the old
 * answer, which is the same class of bug as a filter left on invisibly and just
 * as hard to see.
 *
 * The selection is cleared for the reason `setScope` clears it: forty ticks
 * were an answer to "these ones, out of what I am looking at", and the reader
 * is no longer looking at that.
 */
function afterFilterChange(): void {
  clearSelection();
  void reload();
}

export function toggleFilter(key: filters.Toggle): void {
  filters.toggle(key);
  afterFilterChange();
}

/**
 * Move one or both ends of the date range.
 *
 * A patch rather than both values, because the two `<input type="date">`
 * elements each close over the filters as of the render that produced them. Set
 * one and then the other before Preact re-renders and the second call carries a
 * stale copy of the first end, quietly undoing it -- which is not something a
 * person can do by hand but is exactly what a test, a paste, or an autofill
 * does. Reading the current value here removes the possibility rather than
 * relying on renders landing between keystrokes.
 */
export function setFilterDates(patch: { since?: string; until?: string }): void {
  const f = filters.filters.peek();
  const since = patch.since ?? f.since;
  const until = patch.until ?? f.until;
  if (f.since === since && f.until === until) return;
  filters.setDates(since, until);
  afterFilterChange();
}

export function clearFilters(): void {
  if (!filters.anyActive.peek()) return;
  filters.clear();
  afterFilterChange();
}

let searchTimer: ReturnType<typeof setTimeout> | undefined;

export function setSearch(text: string): void {
  search.value = text;
  clearSelection();
  clearTimeout(searchTimer);
  // Long enough that typing a word is one query rather than five, short enough
  // that it still feels like it is keeping up.
  searchTimer = setTimeout(() => void reload(), 250);
}

export function open(row: Envelope): Promise<void> {
  // The list row already has everything the header needs, so the pane paints
  // immediately and fills in the body when it arrives.
  return begin(row.accountId, row.email.id, row);
}

/**
 * Open a message named only by its ids.
 *
 * For arrivals from outside the list: a link followed into the app, a
 * notification tapped, a reload of a URL that named a message. There is no row
 * to paint from, so the pane shows that it is working until the fetch lands --
 * see `ui/Reader.tsx`, which must not say "no message selected" at somebody who
 * has just asked for a specific one.
 *
 * **The ids are the server's**, and that is the one thing about this function
 * that must not drift. The local replica keys its rows by an entirely different
 * number, and `src-tauri/src/replica/ids.rs` exists because the two were once
 * confused; anything that reaches this from a URL, a notification payload or a
 * window handoff has to be in JMAP's id space by the time it arrives.
 */
export function openById(accountId: string, emailId: string): Promise<void> {
  return begin(accountId, emailId, null);
}

async function begin(
  accountId: string,
  emailId: string,
  seed: Envelope | null,
): Promise<void> {
  const key = `${accountId}:${emailId}`;
  batch(() => {
    openEmailId.value = key;
    cursor.value = key;
    openError.value = null;
    openLoading.value = true;
    openEmail.value = seed;
  });
  try {
    const full = await jmap.getEmail(accountId, emailId);
    if (openEmailId.value === key) openEmail.value = full;
  } catch (e) {
    if (openEmailId.value === key) openError.value = message(e);
  } finally {
    if (openEmailId.value === key) openLoading.value = false;
  }
}

export function closeEmail(): void {
  batch(() => {
    openEmail.value = null;
    openEmailId.value = null;
  });
}

/**
 * Move the keyboard cursor by `delta` rows, opening whatever lands under it.
 *
 * The cursor is tracked separately from the open message even though `j` opens
 * as it goes, because `x` has to have something to tick when nothing is open --
 * on a narrow screen the list is the whole window and there is no reading pane
 * for `openEmailId` to refer to.
 */
export function step(delta: number): void {
  const list = rows.value;
  if (list.length === 0) return;
  const from = cursor.value ?? openEmailId.value;
  const current = list.findIndex((r) => keyOf(r) === from);
  const next = current < 0 ? 0 : Math.min(list.length - 1, Math.max(0, current + delta));
  const row = list[next];
  if (row) void open(row);
}

/** The row the keyboard is on, if it is still in the list. */
export function cursorRow(): Envelope | undefined {
  const key = cursor.value ?? openEmailId.value;
  if (!key) return undefined;
  return rows.value.find((r) => keyOf(r) === key);
}
